import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { format } from "@core-ui/utils/String";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { TimerCroutonTimerBox, TimerCroutonTimerText } from "./styles";
import { CART_TIMER_TEST_IDS } from "@core-ui/constants/TimerOnCart";
import { isEmpty } from "@swiggy-private/js-utils";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

interface CroutonTimerProps {
    duration?: number;
    source?: string;
    onComplete?: () => void;
    onTimerLoad?: (timerContext?: string) => void;
    timerClassName?: string;
    precalculatedTimerDuration?: {
        minutes: number;
        seconds: number;
    };
    textColor?: keyof IColors;
    timerText?: string; // Format string like "{0}m {1}s" where {0}=minutes, {1}=seconds
    onTimeUpdate?: (remainingSeconds: number) => void;
}

export const getMinSecFromDuration = (duration?: number): { seconds: number; minutes: number } => {
    if (!isEmpty(duration) && !Number.isNaN(duration)) {
        const secondsToDisplay = duration % 60;
        const minutesRemaining = (duration - secondsToDisplay) / 60;
        const minutesToDisplay = minutesRemaining % 60;

        return {
            seconds: secondsToDisplay,
            minutes: minutesToDisplay,
        };
    }
    return {
        minutes: 0,
        seconds: 0,
    };
};

const CroutonTimer: React.FC<CroutonTimerProps> = ({
    duration,
    onTimerLoad,
    onComplete,
    timerClassName = "",
    precalculatedTimerDuration,
    textColor,
    timerText = "{0}m {1}s",
    onTimeUpdate,
}) => {
    const isValidDuration = !isEmpty(duration) && !Number.isNaN(duration);
    const DEFAULT_TIME = "0m 0s";
    const TIMER_TEXT = timerText;
    const id = "crouton_timer";
    const timerInterval = useRef<ReturnType<Window["setInterval"]>>();
    const { seconds: initialSeconds, minutes: initialMinutes } = getMinSecFromDuration(duration);
    const [remainingTime, setRemainingTime] = useState({ seconds: initialSeconds, minutes: initialMinutes });

    useEffect(() => {
        if (isValidDuration) {
            const { seconds: updatedSeconds, minutes: updatedMinutes } = getMinSecFromDuration(duration);
            setRemainingTime({ seconds: updatedSeconds, minutes: updatedMinutes });
        }
    }, [duration, isValidDuration]);

    useEffect(() => {
        if (isValidDuration) {
            timerInterval.current = window.setInterval(() => {
                setRemainingTime((prevState) => {
                    const totalSeconds = prevState.minutes * 60 + prevState.seconds;
                    const newTotalSeconds = totalSeconds > 0 ? totalSeconds - 1 : 0;

                    // Call onTimeUpdate with remaining seconds (including 0 to signal completion)
                    if (onTimeUpdate) {
                        onTimeUpdate(newTotalSeconds);
                    }

                    if (prevState.seconds > 0) {
                        return { ...prevState, seconds: prevState.seconds - 1 };
                    }
                    if (prevState.seconds === 0) {
                        if (prevState.minutes === 0) {
                            onComplete?.();
                            if (timerInterval.current != null) {
                                clearInterval(timerInterval.current);
                            }
                            return prevState; // No change to state, the timer is complete
                        } else {
                            return {
                                minutes: prevState.minutes - 1,
                                seconds: 59,
                            };
                        }
                    }
                    return prevState;
                });
            }, 1000);
        }
        return (): void => {
            if (timerInterval.current != null) {
                clearInterval(timerInterval.current);
            }
        };
    }, [onComplete, isValidDuration, onTimeUpdate]);

    useEffectOnce(() => {
        onTimerLoad?.(JSON.stringify({ timer: isValidDuration ? duration : undefined }));
    });

    const incomingFormattedTimerText = useCallback(() => {
        if (!isEmpty(precalculatedTimerDuration)) {
            const { minutes = 0, seconds = 0 } = precalculatedTimerDuration;
            return format(TIMER_TEXT, String(minutes), String(seconds));
        }
        return DEFAULT_TIME;
    }, [precalculatedTimerDuration, TIMER_TEXT]);

    const getTimeInMins = useCallback(() => {
        return format(TIMER_TEXT, String(remainingTime.minutes), String(remainingTime.seconds));
    }, [remainingTime.minutes, remainingTime.seconds, TIMER_TEXT]);

    const colorValue = (textColor as keyof IColors) ?? "background_Primary";

    return (
        <TimerCroutonTimerBox className={timerClassName} data-testid={id}>
            <TimerCroutonTimerText
                variant="V2.body3.neutral.bold"
                color={colorValue}
                data-testid={CART_TIMER_TEST_IDS.CART_TIMER_TEXT}
            >
                {!isEmpty(precalculatedTimerDuration) ? incomingFormattedTimerText() : getTimeInMins()}
            </TimerCroutonTimerText>
        </TimerCroutonTimerBox>
    );
};

export default memo(CroutonTimer);
